import {html} from "lit";
import {SettingsRepository} from "../SettingsRepository.js";
import {ClickUpClient} from "../../lib/ClickUp/ClickUpClient.js";
import {Router} from "@vaadin/router";
import ToastifyEs from "toastify-js/src/toastify-es.js";
import {Page} from "./Page.js";

export class SettingsPage extends Page {

    static properties = {
        apiKey: {reflect: true},
        customerSource: {reflect: true},
        defaultWorkSpaceId: {reflect: true},
    }

    /**
     * @private {SettingsRepository}
     */
    _settingsRepo;

    /**
     * @type {Router}
     */
    _router = null;

    constructor() {
        super();
        this.apiKey = "";
        this.customerSource = "";
        this.defaultWorkSpaceId = "";
    }

    render() {
        return html`
            <div class="container">
                <form onsubmit="return false;">
                    <p>
                        <label for="api-key">ClickUp API Key</label>
                        <input type="password" id="api-key" name="api-key" value="${this.apiKey}" placeholder="Put your API key here" class="error" @change="${this._checkApiKey}">
                        <span class="text-error" id="api-key-error">Please enter your API key</span>
                    </p>
                    <p>
                        <label for="customer-source">Customer CRM Source</label>
                        <select id="customer-source" @change="${this._enableSaveButton}"></select>
                    </p>
                    <p>
                        <button class="button primary" id="submit-button" @click="${this._save}" disabled>Save</button>
                    </p>
                </form>
            </div>`;
    }

    createRenderRoot() {
        return this;
    }

    connectedCallback() {
        super.connectedCallback();
        this._settingsRepo = this._resolve(SettingsRepository);
        this._router = this._resolve(Router);
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);
        try {
            let settings = this._settingsRepo.get();
            this.renderRoot.querySelector("#api-key").value = settings.apiKey;
            this._showAPIKeySuccess(this.renderRoot.querySelector("#api-key"));
            this.renderRoot.querySelector("#customer-source").innerHTML =
                `<option value="${settings.customerSource}">${settings.customerSourceName}</option>`;

        } catch (e) {
            if (e.toString().indexOf('found') === -1) {
                throw e;
            }
        }
    }

    _save() {
        /** @type {HTMLSelectElement} **/
        let customerSelect = this.renderRoot.querySelector("#customer-source");
        /** @type {AppSettings} **/
        let settings = {
            apiKey: this.renderRoot.querySelector("#api-key").value,
            customerSource: customerSelect.value,
            customerSourceName: customerSelect.options[customerSelect.selectedIndex].innerText,
            defaultWorkSpaceId: customerSelect.options[customerSelect.selectedIndex].dataset.workspaceId
        };

        this._settingsRepo.save(settings);

        ToastifyEs({
            text: "Settings saved",
            className: "success",
            duration: 1500,
            position: "right"
        }).showToast();

        Router.go(this._router.urlForPath('/home'));

    }

    _checkApiKey() {
        let apiKeyInput = this.renderRoot.querySelector("#api-key");
        let apiKey = apiKeyInput.value;
        if (apiKey.trim() === "") {
            this._showAPIKeyError(apiKeyInput, 'Please enter your API key');
            this.renderRoot.querySelector("#submit-button").disabled = true;
            return;
        }

        let client = new ClickUpClient(apiKey);

        let candidates = {};

        let workSpacesJob = this._publishJob(client.getWorkspaces());

        workSpacesJob.then(async (workspaces) => {

            this._showAPIKeySuccess(apiKeyInput);

            for (let workspace of workspaces) {

                let spacesJob = this._publishJob(client.getSpaces(workspace));
                workspace.spaces = await spacesJob;

                for (let space of workspace.spaces) {
                    let listJob = this._publishJob(client.getFolderLessLists(space.id));
                    for (let folderLessList of await listJob) {
                        candidates[folderLessList.id] = {
                            name: `${workspace.name} > ${space.name} > ${folderLessList.name}`,
                            workSpaceId: workspace.id,
                        }
                    }
                }
            }

            let options = [];
            options.push('<option value="">Choose...</option>');
            for (let candidateId of Object.keys(candidates)) {
                options.push(`<option value="${candidateId}" data-workspace-id="${candidates[candidateId].workSpaceId}">${candidates[candidateId].name}</option>`)
            }

            this.renderRoot.querySelector("#customer-source").innerHTML = options.join('\n');

            if (options.length === 1) {
                this.renderRoot.querySelector("#submit-button").disabled = false;
            }

        }).catch((err) => {
            this._showAPIKeyError(apiKeyInput, 'Unable to read from ClickUp API. Invalid API key?');
            console.error('ClickUp API error:', err);
        })

    }

    _enableSaveButton() {
        this.renderRoot.querySelector("#submit-button").disabled = false;
    }

    _showAPIKeySuccess(apiKeyInput) {
        apiKeyInput.classList.remove('error');
        apiKeyInput.classList.add('success');
        this.renderRoot.querySelector('#api-key-error').innerText = '';
    }

    /**
     * @param apiKeyInput
     * @param {string} message
     * @private
     */
    _showAPIKeyError(apiKeyInput, message) {
        apiKeyInput.classList.add('error');
        this.renderRoot.querySelector('#api-key-error').innerText = message;
    }
}

customElements.define('x-settings-page', SettingsPage);